import React, { useEffect, useRef, useState } from "react";
import logo from "../assets/logo.png";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { useView } from "../contexts/ViewContext";
import DateRangePicker from "./DateRangePicker";
import { getDatePickerState } from "./header/datePicker";

export default function ClientHeader({
  username = "client_user",
  initials = "CL",
  activePage = "",
  // 2026-08-25 — optional slot for page-injected content that should appear
  // beside the Property dropdown in the header (e.g. an extra control a page
  // wants surfaced globally). Defaults to null so pages that don't pass it
  // see no change. Rendered in both the ≥1700px inline cluster and the
  // <1700px selectors row, immediately after the Property dropdown. Generic —
  // not tied to any specific page or feature.
  rightSlot = null,
}) {
  const [showLogout, setShowLogout] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const { signOut } = useAuth();
  const avatar = localStorage.getItem('avatar') || null;
  // Global date-range picker state for this route — one resolver call instead
  // of composing hidesGlobalDateRange + disablesGlobalDateRange + a hand-
  // written hint string. The hint text lives in DISABLE_REASONS (in
  // ./header/datePicker) so both headers share one source of truth and can't
  // drift apart the way they did when each inlined its own copy.
  const { state: datePickerState, disabledReason: datePickerHint } = getDatePickerState(location.pathname);
  const hideDatePicker = datePickerState === 'hidden';
  const disableDatePicker = datePickerState === 'disabled';

  const {
    clientProjects, loadingProjects,
    selectedProjectId, selectProject,
    rangeStart, rangeEnd, datePreset, setDateRange,
  } = useView();

  // On the URL-scoped transcript page (/client-projects/:id), switching the
  // property must also move the URL — otherwise the page keeps showing the
  // old property's transcripts while the dropdown shows the new one.
  const handleProjectChange = (id) => {
    selectProject(id);
    if (location.pathname.startsWith("/client-projects/")) {
      navigate(`/client-projects/${id}${location.search}`);
    }
  };

  // Publish the header's measured height as a CSS variable so sticky/fixed
  // children (NavSidebar, LiveFeed) can offset themselves correctly when the
  // header wraps to multiple rows on narrow viewports.
  const headerRef = useRef(null);
  useEffect(() => {
    if (!headerRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      document.documentElement.style.setProperty(
        '--app-header-h',
        `${Math.round(entry.contentRect.height)}px`,
      );
    });
    ro.observe(headerRef.current);
    return () => ro.disconnect();
  }, []);

  // 2026-07-28 — manual localStorage clears used to be all this did, but that
  // left the Supabase Auth session (in-memory + Supabase's own localStorage
  // keys under `sb-*-auth-token`) intact — so `supabase.auth.getSession()`
  // still returned the previous user, and every RPC continued on the stale
  // JWT. Route through AuthContext.signOut(), which calls
  // supabase.auth.signOut() first, then clears legacy keys. Match AdminHeader.
  const handleLogout = async () => {
    setShowLogout(false);
    await signOut();
    navigate("/");
  };

  return (
    <header ref={headerRef} className="sticky top-0 z-40 bg-white dark:bg-gray-900 shadow-sm dark:shadow-[0_4px_10px_-2px_rgba(0,0,0,0.6)] transition-colors duration-200">
      <div className="flex justify-between items-center gap-2 sm:gap-3 px-3 sm:px-4 md:px-6 py-2.5 sm:py-3 md:py-4">
        <div className="flex items-center gap-2 sm:gap-4 md:gap-6 min-w-0">
          <img src={logo} alt="Insider Perks" className="h-7 sm:h-8 md:h-10 w-auto max-w-[110px] sm:max-w-none shrink-0" />

          <nav className="flex items-center gap-1">
            <Link
              to="/client-dashboard"
              className={`px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center gap-2 ${
                activePage === "Dashboard" || activePage === ""
                  ? "bg-emerald-500 text-white"
                  : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              }`}
            >
              Dashboard
            </Link>
          </nav>
        </div>

        {/* Center slot — pages portal scroll-promoted filters here (e.g. Google Ads campaign filters) */}
        <div id="app-header-center-slot" className="hidden lg:flex items-center justify-center flex-1 min-w-0 px-4" />

        <div className="flex items-center gap-0.5 sm:gap-1.5 md:gap-3">
          {/* Global: Project → Date (≥1500px only, otherwise on second row) */}
          {clientProjects.length > 0 && (
            <div className="hidden min-[1700px]:flex items-center gap-2">
              <select
                value={selectedProjectId}
                onChange={(e) => handleProjectChange(e.target.value)}
                disabled={loadingProjects}
                className="text-sm border dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium focus:ring-2 focus:ring-[#32B1FF] focus:border-transparent outline-none max-w-[260px]"
              >
                {clientProjects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              {rightSlot}
              {!hideDatePicker && (
                <DateRangePicker
                  startDate={rangeStart}
                  endDate={rangeEnd}
                  activePreset={datePreset}
                  onApply={(start, end, preset) => setDateRange(start, end, preset)}
                  disabled={disableDatePicker}
                  hint={datePickerHint}
                />
              )}
            </div>
          )}

          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
          >
            {isDarkMode ? (
              <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
              </svg>
            )}
          </button>

          <Link
            to="/profile"
            className="flex items-center gap-2 md:gap-3 px-1 md:px-3 py-2 md:-mx-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
            title="View Profile"
          >
            <div className={`h-9 w-9 md:h-10 md:w-10 rounded-full flex items-center justify-center text-white text-base md:text-lg font-bold overflow-hidden shrink-0 ${
              activePage === "Profile"
                ? "ring-2 ring-emerald-500 ring-offset-2 dark:ring-offset-gray-900"
                : ""
            } ${avatar ? "" : "bg-emerald-500"}`}>
              {avatar ? (
                <img src={avatar} alt="" className="w-full h-full object-cover" />
              ) : (
                initials
              )}
            </div>
            <div className="hidden xl:flex flex-col min-w-0">
              <span className="text-gray-800 dark:text-gray-200 font-medium text-sm truncate">{username}</span>
              <span className="text-emerald-600 dark:text-emerald-400 text-xs font-medium">Client</span>
            </div>
          </Link>

          <button
            className="hidden sm:inline-block px-3 md:px-4 py-1.5 md:py-2 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded font-semibold hover:bg-red-200 dark:hover:bg-red-900/50 border border-red-200 dark:border-red-800 shadow-sm transition text-sm"
            onClick={() => setShowLogout(true)}
          >
            Logout
          </button>
          <button
            className="sm:hidden p-2 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 border border-red-200 dark:border-red-800 transition"
            onClick={() => setShowLogout(true)}
            title="Logout"
            aria-label="Logout"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </div>

      {/* Selectors row (visible below 1700px) */}
      {clientProjects.length > 0 && (
        <div className="min-[1700px]:hidden border-t border-gray-100 dark:border-gray-800 px-3 sm:px-4 md:pl-[50px] md:pr-6 py-2 flex flex-col min-[500px]:flex-row min-[500px]:flex-wrap min-[500px]:items-center gap-2">
          <select
            value={selectedProjectId}
            onChange={(e) => handleProjectChange(e.target.value)}
            disabled={loadingProjects}
            className="w-full min-[500px]:flex-1 min-[500px]:min-w-0 min-[900px]:max-w-[260px] text-sm border dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium focus:ring-2 focus:ring-[#32B1FF] focus:border-transparent outline-none"
          >
            {clientProjects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          {rightSlot}
          {!hideDatePicker && (
            <DateRangePicker
              className="w-full min-[500px]:flex-1 min-[500px]:min-w-0 min-[900px]:w-auto"
              triggerClassName="w-full justify-between min-[900px]:justify-start"
              startDate={rangeStart}
              endDate={rangeEnd}
              activePreset={datePreset}
              onApply={(start, end, preset) => setDateRange(start, end, preset)}
              disabled={disableDatePicker}
              hint={datePickerHint}
            />
          )}
        </div>
      )}

      {showLogout && (
        <div className="fixed inset-0 bg-black bg-opacity-30 dark:bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8 w-full max-w-xs">
            <h2 className="text-lg font-bold mb-2 text-gray-800 dark:text-gray-100">Logout</h2>
            <div className="mb-5 text-gray-600 dark:text-gray-300">Are you sure you want to logout?</div>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200"
                onClick={() => setShowLogout(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-red-600 dark:bg-red-700 text-white font-semibold hover:bg-red-700 dark:hover:bg-red-800"
                onClick={handleLogout}
              >
                Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
